import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Pencil } from "lucide-react";

// Fallback copy function for browsers that don't support clipboard API
const copyToClipboard = async (text) => {
  // Try modern clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fall through to fallback
    }
  }
  
  // Fallback: Create a temporary textarea
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    document.execCommand("copy");
    textArea.remove();
    return true;
  } catch (err) {
    textArea.remove();
    return false;
  }
};

export default function AlternativeCard({ alternative, index }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(alternative.text);
    
    if (success) {
      setCopied(true);
      toast.success("Copied to clipboard ✓");
      
      // Show personalization tip after a short delay
      setTimeout(() => {
        toast(
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#EAF6F4] flex items-center justify-center flex-shrink-0">
              <Pencil size={14} className="text-[#4FB3A5]" />
            </div>
            <div>
              <p className="font-medium text-[#1F3A5F] text-sm">Tip</p>
              <p className="text-[#6B7280] text-xs mt-0.5">Tweak this so it sounds like you. A personal touch makes it more authentic.</p>
            </div>
          </div>,
          {
            duration: 4000,
            className: "bg-white border border-[#E5E7EB]",
          }
        );
      }, 800);
      
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const toneStyles = {
    calm: { bg: "bg-[#EAF6F4]", text: "text-[#4FB3A5]" },
    assertive: { bg: "bg-[#FEF9E7]", text: "text-[#B8860B]" },
    vulnerable: { bg: "bg-[#FDF2F8]", text: "text-[#DB2777]" },
    empathetic: { bg: "bg-[#ECFDF5]", text: "text-[#059669]" },
    direct: { bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
    warm: { bg: "bg-[#FFF7ED]", text: "text-[#EA580C]" },
    understanding: { bg: "bg-[#EDE9FE]", text: "text-[#7C3AED]" },
    curious: { bg: "bg-[#E0F2FE]", text: "text-[#0284C7]" },
    supportive: { bg: "bg-[#ECFDF5]", text: "text-[#059669]" },
    honest: { bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
    gentle: { bg: "bg-[#FDF2F8]", text: "text-[#DB2777]" }
  };

  const getToneStyle = (tone) => {
    const lowerTone = tone.toLowerCase();
    for (const [key, value] of Object.entries(toneStyles)) {
      if (lowerTone.includes(key)) return value;
    }
    return { bg: "bg-[#F1F5F9]", text: "text-[#6B7280]" };
  };

  const style = getToneStyle(alternative.tone);

  return (
    <div 
      className="cursor-pointer bg-white border-2 border-[#E5E7EB] rounded-xl p-5
                 transition-all duration-300 hover:border-[#7BC4A4] hover:shadow-soft
                 group animate-fadeIn"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={handleCopy}
      data-testid={`alternative-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <Badge 
          className={`${style.bg} ${style.text} border-0 text-xs font-semibold py-1.5 px-3 rounded-lg`}
          data-testid={`tone-badge-${index}`}
        >
          {alternative.tone}
        </Badge>
        <div className="flex items-center gap-1.5 text-[#A0A7B4] group-hover:text-[#7BC4A4] transition-colors">
          {copied ? (
            <Check size={16} className="text-[#7BC4A4]" />
          ) : (
            <Copy size={16} />
          )}
          <span className="text-xs font-medium">{copied ? "Copied!" : "Click to copy"}</span>
        </div>
      </div>
      
      <p 
        className="text-[#2C2C2C] leading-relaxed mb-4 bg-[#F7F9FB] p-4 rounded-xl border border-[#E5E7EB]"
        data-testid={`alternative-text-${index}`}
      >
        "{alternative.text}"
      </p>
      
      <p className="text-sm text-[#6B7280]" data-testid={`alternative-explanation-${index}`}>
        <span className="font-semibold text-[#1F3A5F]">Why this works: </span>
        {alternative.explanation}
      </p>
    </div>
  );
}
